import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { SupportedLocale } from '../../utils/routing/locale-from-pathname';
import { deriveSortedCvEntries } from '../../utils/profile/derive-cv-entries';
import { normalizeDesiredLocations } from '../../utils/profile/normalize-desired-locations';
import { coerceCvStyle, DEFAULT_CV_STYLE, type CvStyle } from '../../utils/resource-pages/cv-template/cv-style';

/** CV content (title/experience/education/projects/skills) is kept independently per language. */
export type ProfileLocale = SupportedLocale;
export const PROFILE_LOCALES: ProfileLocale[] = ['en', 'fi', 'sv'];

export interface Company {
  name: string;
  website: string;
  logoUrl: string;
  about: string;
  address: string;
  companyValues: string[];
  numberOfEmployees: number;
  industry: string;
  contactDetails: string;
}

export interface JobDetails {
  title: string;
  normalisedTitle: string;
  normalisedRoleLevel: string;
  description: string;
  locations: string[];
  remoteness: string;
  jobType: string;
  salary: number;
  /** Current work percentage (1–100). Used to compute 100% FTE equivalent for match score. */
  workPercentage: number;
  vacationWeeks: number;
  benefits: string[];
  /** Languages the user speaks at professional level (for match score). */
  languages: string[];
  company: Company;
}

export interface ProfileWorkExperience {
  id: string;
  startDate: string;
  endDate: string;
  title: string;
  company: string;
  description?: string;
}

export interface ProfileEducation {
  id: string;
  startDate: string;
  endDate: string;
  degree: string;
  institution: string;
}

export interface ProfileProject {
  id: string;
  year: string;
  title: string;
  description: string;
}

/** CV content for a single language. Independently edited/filled per {@link ProfileLocale}. */
export interface ProfileCvContent {
  profileTitle: string;
  /** 1 to 3 sentence summary, independently written per locale (not auto-translated). */
  profileSummary: string;
  profileWorkExperiences: ProfileWorkExperience[];
  profileEducation: ProfileEducation[];
  profileProjects: ProfileProject[];
  profileSkills: string[];
}

export type ProfileCvByLocale = Record<ProfileLocale, ProfileCvContent>;

export const LANGUAGE_LEVELS = ['native', 'c2', 'c1', 'b2', 'b1', 'a2', 'a1'] as const;
export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];

/** A language the user speaks, shared across locales (not per-locale CV content). */
export interface ProfileLanguage {
  language: string;
  level: LanguageLevel;
}

export type AvailabilityStatus = 'actively_looking' | 'open' | 'not_looking' | '';
export const AVAILABILITY_STATUSES: AvailabilityStatus[] = ['actively_looking', 'open', 'not_looking', ''];

export type NoticePeriod = '' | 'immediately' | 'two_weeks' | 'one_month' | 'two_months' | 'three_months' | 'longer';
export const NOTICE_PERIODS: NoticePeriod[] = ['', 'immediately', 'two_weeks', 'one_month', 'two_months', 'three_months', 'longer'];

export interface Availability {
  status: AvailabilityStatus;
  noticePeriod: NoticePeriod;
  /** ISO date, or '' if unset. */
  earliestStart: string;
}

export interface RecruiterVisibility {
  enabled: boolean;
  consentAt: string | null;
}

/**
 * The profile sections the redesigned page (docs/profile-redesign-spec.md) tracks a
 * "last saved" timestamp for. Every reducer that mutates a section's data stamps
 * `sectionUpdatedAt[section]` via {@link stampSectionUpdated} so the UI can show a live
 * "Saved just now" status line per section without re-deriving it from the sync hook.
 */
export type ProfileSection =
  | 'header'
  | 'summary'
  | 'experience'
  | 'education'
  | 'projects'
  | 'skills'
  | 'languages'
  | 'licences'
  | 'matching'
  | 'importance'
  | 'applying'
  | 'currentPay'
  | 'homeAddress';

export const PROFILE_SECTIONS: ProfileSection[] = [
  'header',
  'summary',
  'experience',
  'education',
  'projects',
  'skills',
  'languages',
  'licences',
  'matching',
  'importance',
  'applying',
  'currentPay',
  'homeAddress',
];

/**
 * A saved starting point for the map's commute search (SRP map view), so it can default there
 * instead of always requiring a map click. Geocoded client-side when the user enters it in their
 * profile (see Profile.tsx), only the resulting label + coordinates are stored here, same as any
 * other profile field, and synced to UserState like the rest of this slice.
 */
export interface HomeAddress {
  label: string;
  point: { lat: number; lng: number };
}

/** How the user usually gets to work; picks which travel time leads on the job page. Null = public transport. */
export type CommuteMode = 'transit' | 'driving' | 'cycling' | 'walking';
export const COMMUTE_MODES: CommuteMode[] = ['transit', 'driving', 'cycling', 'walking'];

/**
 * Work-authorization status for Finland/EU, used to answer the near-universal screening question
 * every ATS application form asks (confirmed live on Teamtailor's own `answers_attributes` custom
 * questions during TEC-489 discovery). Deliberately a fixed status, not free text: at fill time the
 * autofill recipe/LLM matches this status against whatever exact radio-button wording a given
 * company's form uses, rather than us trying to predict every company's phrasing here.
 */
export type WorkAuthorizationStatus =
  | ''
  | 'eu_eea_citizen'
  | 'finnish_citizen'
  | 'permanent_resident'
  | 'valid_work_permit'
  | 'requires_sponsorship'
  | 'not_authorized';

/**
 * Contact + application-specific details a CV parse doesn't naturally produce (confirmed by
 * reading profile-cv.ts's actual extraction schema during TEC-489 planning: no name/email/phone/
 * work-authorization/salary fields exist anywhere in the profile today). Powers Application
 * Autofill (TEC-489) form-fill in addition to being generally useful contact info. `email`
 * defaults from the Auth0 session on first use (see Profile.tsx) but is independently stored/
 * editable, a user may want a different application-facing address.
 */
export interface ApplicationDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  workAuthorization: WorkAuthorizationStatus;
  /** Annual salary expectation in the user's local currency, 0 = unset. */
  salaryExpectation: number;
}

export interface JobFormState {
  /** User-uploaded profile picture (JPEG data URL). Overrides OAuth avatar when set. */
  profilePhotoDataUrl: string;
  /** S3 object key of the uploaded CV PDF (cv-uploads/{userId}/{uuid}.pdf), not a URL. */
  cvFileKey: string;
  linkedInUrl: string;
  /** CV content (title/experience/education/projects/skills), independent per language, not auto-translated. */
  profileCvByLocale: ProfileCvByLocale;
  /** Optional saved commute-search starting point; null until the user sets one. */
  homeAddress: HomeAddress | null;
  /** Preferred way of commuting, shown first on the job page's travel-time strip; null means public transport. */
  preferredCommuteMode: CommuteMode | null;
  /** Contact + application details (TEC-489 Application Autofill), independent of CV content. */
  applicationDetails: ApplicationDetails;
  currentJob: JobDetails;
  desiredJob: {
    titles: string[];
    normalisedRoleLevels: string[];
    /** Gazetteer placeIds (TEC-482 regionTags): the canonical desired locations. */
    regionTags: string[];
    /** Legacy flat location strings that could not be resolved to a regionTag; kept so nothing typed is lost. */
    locations: string[];
    remoteness: string[];
    jobTypes: string[];
    salaryMin: number;
    vacationWeeks: number;
    benefits: string[];
    companyIndustries: string[];
  };
  /** Languages the user speaks, shared across locales (not per-locale CV content). */
  profileLanguages: ProfileLanguage[];
  /** Licences/certifications (driving licence B, hygieniapassi, etc.), shared across locales. */
  profileLicences: string[];
  availability: Availability;
  recruiterVisibility: RecruiterVisibility;
  sectionUpdatedAt: Partial<Record<ProfileSection, string>>;
  /** CV template the user picked (TEC-711), one of CV_TEMPLATE_IDS; '' means the default (basic). */
  cvTemplateId: string;
  /** CV check rule ids the user marked "not relevant for me"; excluded from the score. */
  cvCheckDismissed: string[];
  /** Named copies of the CV (TEC-711), e.g. "Sales" and "Marketing". At most MAX_CV_VERSIONS. */
  cvVersions: CvVersion[];
  /** Accent colour and typeface for the user's own CV (TEC-711). */
  cvStyle: CvStyle;
}

export const MAX_CV_VERSIONS = 10;

export interface CvVersion {
  id: string;
  name: string;
  locale: ProfileLocale;
  templateId: string;
  /** ISO timestamp. */
  savedAt: string;
  content: ProfileCvContent;
}

const initialJobDetails: JobDetails = {
  title: '',
  normalisedTitle: '',
  normalisedRoleLevel: '',
  description: '',
  locations: [],
  remoteness: '',
  jobType: '',
  salary: 0,
  workPercentage: 100,
  vacationWeeks: 0,
  benefits: [],
  languages: [],
  company: {
    name: '',
    website: '',
    logoUrl: '',
    about: '',
    address: '',
    companyValues: [],
    numberOfEmployees: 0,
    industry: '',
    contactDetails: '',
  },
};

function emptyProfileCvContent(): ProfileCvContent {
  return {
    profileTitle: '',
    profileSummary: '',
    profileWorkExperiences: [],
    profileEducation: [],
    profileProjects: [],
    profileSkills: [],
  };
}

function defaultProfileCvByLocale(): ProfileCvByLocale {
  return { en: emptyProfileCvContent(), fi: emptyProfileCvContent(), sv: emptyProfileCvContent() };
}

/** Defensively coerces a possibly-malformed persisted CV content bucket, same spirit as the old flat-field coercion below. */
function coerceProfileCvContent(value: any): ProfileCvContent {
  return {
    profileTitle: typeof value?.profileTitle === 'string' ? value.profileTitle : '',
    profileSummary: typeof value?.profileSummary === 'string' ? value.profileSummary : '',
    profileWorkExperiences: Array.isArray(value?.profileWorkExperiences) ? value.profileWorkExperiences : [],
    profileEducation: Array.isArray(value?.profileEducation) ? value.profileEducation : [],
    profileProjects: Array.isArray(value?.profileProjects) ? value.profileProjects : [],
    profileSkills: Array.isArray(value?.profileSkills) ? value.profileSkills : [],
  };
}

const VALID_LANGUAGE_LEVELS = new Set<string>(LANGUAGE_LEVELS);

function coerceProfileLanguages(value: any): ProfileLanguage[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry): entry is { language: string; level: string } =>
        Boolean(entry) && typeof entry.language === 'string' && entry.language.trim().length > 0
    )
    .map((entry) => ({
      language: entry.language,
      level: VALID_LANGUAGE_LEVELS.has(entry.level) ? (entry.level as LanguageLevel) : 'b2',
    }));
}

function coerceProfileLicences(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
}

const VALID_AVAILABILITY_STATUSES = new Set<string>(AVAILABILITY_STATUSES);
const VALID_NOTICE_PERIODS = new Set<string>(NOTICE_PERIODS);

function emptyAvailability(): Availability {
  return { status: '', noticePeriod: '', earliestStart: '' };
}

function coerceAvailability(value: any): Availability {
  return {
    status: VALID_AVAILABILITY_STATUSES.has(value?.status) ? value.status : '',
    noticePeriod: VALID_NOTICE_PERIODS.has(value?.noticePeriod) ? value.noticePeriod : '',
    earliestStart: typeof value?.earliestStart === 'string' ? value.earliestStart : '',
  };
}

function emptyRecruiterVisibility(): RecruiterVisibility {
  return { enabled: false, consentAt: null };
}

function coerceRecruiterVisibility(value: any): RecruiterVisibility {
  return {
    enabled: value?.enabled === true,
    consentAt: typeof value?.consentAt === 'string' ? value.consentAt : null,
  };
}

function coerceSectionUpdatedAt(value: any): Partial<Record<ProfileSection, string>> {
  if (!value || typeof value !== 'object') return {};
  const result: Partial<Record<ProfileSection, string>> = {};
  for (const section of PROFILE_SECTIONS) {
    const stamp = value[section];
    if (typeof stamp === 'string') result[section] = stamp;
  }
  return result;
}

function emptyApplicationDetails(): ApplicationDetails {
  return { firstName: '', lastName: '', email: '', phone: '', workAuthorization: '', salaryExpectation: 0 };
}

const VALID_WORK_AUTHORIZATION_STATUSES: WorkAuthorizationStatus[] = [
  '',
  'eu_eea_citizen',
  'finnish_citizen',
  'permanent_resident',
  'valid_work_permit',
  'requires_sponsorship',
  'not_authorized',
];

function coerceApplicationDetails(value: any): ApplicationDetails {
  const workAuthorization: WorkAuthorizationStatus = VALID_WORK_AUTHORIZATION_STATUSES.includes(value?.workAuthorization)
    ? value.workAuthorization
    : '';
  return {
    firstName: typeof value?.firstName === 'string' ? value.firstName : '',
    lastName: typeof value?.lastName === 'string' ? value.lastName : '',
    email: typeof value?.email === 'string' ? value.email : '',
    phone: typeof value?.phone === 'string' ? value.phone : '',
    workAuthorization,
    salaryExpectation: typeof value?.salaryExpectation === 'number' ? value.salaryExpectation : 0,
  };
}

function coerceCommuteMode(value: any): CommuteMode | null {
  return typeof value === 'string' && (COMMUTE_MODES as string[]).includes(value) ? (value as CommuteMode) : null;
}

function coerceHomeAddress(value: any): HomeAddress | null {
  if (
    value &&
    typeof value === 'object' &&
    typeof value.label === 'string' &&
    value.point &&
    typeof value.point.lat === 'number' &&
    typeof value.point.lng === 'number'
  ) {
    return { label: value.label, point: { lat: value.point.lat, lng: value.point.lng } };
  }
  return null;
}

/**
 * Builds the per-locale CV container from persisted state, whichever shape it's in:
 * - Already has `profileCvByLocale` (current shape), coerce each bucket defensively.
 * - Old flat shape (`profileTitle`/`profileWorkExperiences`/... at the top level, from before
 *   per-language CVs existed), one-time migration: old single-language content becomes the
 *   `en` bucket, `fi`/`sv` start empty. The user re-fills those themselves (no auto-translation).
 */
function buildProfileCvByLocaleFromPersisted(persisted: any): ProfileCvByLocale {
  if (persisted?.profileCvByLocale && typeof persisted.profileCvByLocale === 'object') {
    return {
      en: coerceProfileCvContent(persisted.profileCvByLocale.en),
      fi: coerceProfileCvContent(persisted.profileCvByLocale.fi),
      sv: coerceProfileCvContent(persisted.profileCvByLocale.sv),
    };
  }
  return {
    en: coerceProfileCvContent(persisted),
    fi: emptyProfileCvContent(),
    sv: emptyProfileCvContent(),
  };
}

function coerceCvTemplateId(value: unknown): string {
  return typeof value === 'string' && value.length <= 32 ? value : '';
}

function coerceCvVersions(value: unknown): CvVersion[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((v): v is CvVersion => Boolean(v) && typeof v === 'object' && typeof (v as CvVersion).id === 'string' && typeof (v as CvVersion).name === 'string')
    .slice(0, MAX_CV_VERSIONS)
    .map((v) => ({
      id: v.id,
      name: v.name.slice(0, 60),
      locale: (PROFILE_LOCALES as readonly string[]).includes(v.locale) ? v.locale : 'en',
      templateId: typeof v.templateId === 'string' ? v.templateId : '',
      savedAt: typeof v.savedAt === 'string' ? v.savedAt : new Date(0).toISOString(),
      content: coerceProfileCvContent(v.content ?? {}),
    }));
}

function coerceStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

const initialState: JobFormState = {
  profilePhotoDataUrl: '',
  cvFileKey: '',
  linkedInUrl: '',
  profileCvByLocale: defaultProfileCvByLocale(),
  homeAddress: null,
  preferredCommuteMode: null,
  applicationDetails: emptyApplicationDetails(),
  currentJob: initialJobDetails,
  desiredJob: {
    titles: [],
    normalisedRoleLevels: [],
    regionTags: [],
    locations: [],
    remoteness: [],
    jobTypes: [],
    salaryMin: 0,
    vacationWeeks: 0,
    benefits: [],
    companyIndustries: [],
  },
  profileLanguages: [],
  profileLicences: [],
  availability: emptyAvailability(),
  recruiterVisibility: emptyRecruiterVisibility(),
  sectionUpdatedAt: {},
  cvTemplateId: '',
  cvCheckDismissed: [],
  cvVersions: [],
  cvStyle: DEFAULT_CV_STYLE,
};

/**
 * Migration: if `profileLanguages` is empty but the legacy `currentJob.languages` (language
 * names only, no level) has values, seed it from there at level 'b2'. Dedupes by lowercased
 * name. Never overwrites existing `profileLanguages` entries.
 */
function seedProfileLanguagesFromCurrentJob(
  profileLanguages: ProfileLanguage[],
  currentJobLanguages: string[]
): ProfileLanguage[] {
  if (profileLanguages.length > 0) return profileLanguages;
  const names = (currentJobLanguages || []).filter((l): l is string => typeof l === 'string' && l.trim().length > 0);
  if (names.length === 0) return profileLanguages;
  const seen = new Set<string>();
  const seeded: ProfileLanguage[] = [];
  for (const language of names) {
    const key = language.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    seeded.push({ language, level: 'b2' });
  }
  return seeded;
}

/** Mirrors `profileLanguages` (language names only) into `currentJob.languages` so calculate-job-match-score.ts needs no change. */
function mirrorLanguagesIntoCurrentJob(profileLanguages: ProfileLanguage[]): string[] {
  return profileLanguages.map((l) => l.language);
}

/** Fills missing desiredJob keys and migrates legacy location strings to regionTags. */
function coerceDesiredJob(raw: any): JobFormState['desiredJob'] {
  const merged = { ...initialState.desiredJob, ...(raw && typeof raw === 'object' ? raw : {}) };
  const { regionTags, locations, impliedRemoteness } = normalizeDesiredLocations(merged);
  const remoteness = Array.isArray(merged.remoteness) ? merged.remoteness : [];
  return {
    ...merged,
    regionTags,
    locations,
    // A legacy "Remote - X" location carried the remote intent too; keep it unless the user already chose a work model.
    remoteness: impliedRemoteness && remoteness.length === 0 ? [impliedRemoteness] : remoteness,
  };
}

// Helper function to migrate old state structure
function migrateState(state: any): JobFormState {
  // If state already has new structure, return as is (with CV section defaults if missing)
  if (state.currentJob && typeof state.currentJob === 'object') {
    return {
      profilePhotoDataUrl: state.profilePhotoDataUrl || '',
      cvFileKey: state.cvFileKey || '',
      linkedInUrl: state.linkedInUrl || '',
      profileCvByLocale: buildProfileCvByLocaleFromPersisted(state),
      homeAddress: coerceHomeAddress(state.homeAddress),
      preferredCommuteMode: coerceCommuteMode(state.preferredCommuteMode),
      applicationDetails: coerceApplicationDetails(state.applicationDetails),
      currentJob: { ...initialJobDetails, ...state.currentJob, workPercentage: state.currentJob.workPercentage ?? 100, languages: Array.isArray(state.currentJob.languages) ? state.currentJob.languages : [] },
      desiredJob: coerceDesiredJob(state.desiredJob),
      profileLanguages: seedProfileLanguagesFromCurrentJob(
        coerceProfileLanguages(state.profileLanguages),
        Array.isArray(state.currentJob.languages) ? state.currentJob.languages : []
      ),
      profileLicences: coerceProfileLicences(state.profileLicences),
      availability: coerceAvailability(state.availability),
      recruiterVisibility: coerceRecruiterVisibility(state.recruiterVisibility),
      sectionUpdatedAt: coerceSectionUpdatedAt(state.sectionUpdatedAt),
      cvTemplateId: coerceCvTemplateId(state.cvTemplateId),
      cvCheckDismissed: coerceStringList(state.cvCheckDismissed),
      cvVersions: coerceCvVersions(state.cvVersions),
      cvStyle: coerceCvStyle(state.cvStyle),
    };
  }

  // Migrate from old structure
  // Handle migration of salary fields: convert salaryRangeMin/Max to salary or salaryMin
  const currentJobSalary = typeof state.salaryRangeMin === 'number' && state.salaryRangeMin > 0
    ? state.salaryRangeMin
    : (typeof state.salary === 'number' ? state.salary : 0);

  const desiredJobSalaryMin = state.desiredJob?.salaryRangeMin
    ? (typeof state.desiredJob.salaryRangeMin === 'number' ? state.desiredJob.salaryRangeMin : 0)
    : (state.desiredJob?.salaryMin
      ? (typeof state.desiredJob.salaryMin === 'number' ? state.desiredJob.salaryMin : 0)
      : 0);

  return {
    profilePhotoDataUrl: state.profilePhotoDataUrl || '',
    // Old cvUrl was a session-only blob URL, never persisted usefully, don't carry it forward.
    cvFileKey: '',
    linkedInUrl: state.linkedInUrl || '',
    // This branch is for the pre-CV-sections shape (no profileTitle/profileWorkExperiences either), so there's nothing to migrate into locale buckets.
    profileCvByLocale: defaultProfileCvByLocale(),
    homeAddress: coerceHomeAddress(state.homeAddress),
    preferredCommuteMode: coerceCommuteMode(state.preferredCommuteMode),
    applicationDetails: coerceApplicationDetails(state.applicationDetails),
    currentJob: {
      title: state.title || '',
      normalisedTitle: state.normalisedTitle || '',
      normalisedRoleLevel: state.normalisedRoleLevel || '',
      description: state.description || '',
      locations: Array.isArray(state.locations) ? state.locations : [],
      remoteness: state.remoteness || '',
      jobType: state.jobType || '',
      salary: currentJobSalary,
      workPercentage: 100,
      vacationWeeks: typeof state.vacationWeeks === 'number' ? state.vacationWeeks : 0,
      benefits: Array.isArray(state.benefits) ? state.benefits : [],
      languages: [],
      company: state.company && typeof state.company === 'object' ? {
        name: state.company.name || '',
        website: state.company.website || '',
        logoUrl: state.company.logoUrl || '',
        about: state.company.about || '',
        address: state.company.address || '',
        companyValues: Array.isArray(state.company.companyValues) ? state.company.companyValues : [],
        numberOfEmployees: typeof state.company.numberOfEmployees === 'number' ? state.company.numberOfEmployees : 0,
        industry: state.company.industry || '',
        contactDetails: state.company.contactDetails || '',
      } : initialJobDetails.company,
    },
    desiredJob: coerceDesiredJob(state.desiredJob ? { ...state.desiredJob, salaryMin: desiredJobSalaryMin } : undefined),
    profileLanguages: [],
    profileLicences: [],
    availability: emptyAvailability(),
    recruiterVisibility: emptyRecruiterVisibility(),
    sectionUpdatedAt: {},
    cvTemplateId: '',
    cvCheckDismissed: [],
    cvVersions: [],
    cvStyle: DEFAULT_CV_STYLE,
  };
}

/** Stamps `sectionUpdatedAt[section]` with the current time. Called from every reducer that mutates that section's data (see {@link ProfileSection}). */
function stampSectionUpdated(state: JobFormState, section: ProfileSection): void {
  state.sectionUpdatedAt[section] = new Date().toISOString();
}

/**
 * The most recently held role, derived from the active locale's CV (falling back to any locale
 * with content) rather than asked separately. Powers `ProfileHeader`'s headline and
 * `currentJob.title`'s auto-follow behaviour (see setProfileCvField).
 */
export function selectDerivedCurrentRole(
  state: { jobForm: JobFormState },
  locale: ProfileLocale
): { title: string; company: string } | null {
  const activeCv = state.jobForm.profileCvByLocale[locale];
  const activeMostRecent = deriveSortedCvEntries({
    workExperiences: activeCv.profileWorkExperiences,
    education: activeCv.profileEducation,
    projects: activeCv.profileProjects,
  }).mostRecentWorkExp;
  if (activeMostRecent) return activeMostRecent;

  for (const fallbackLocale of PROFILE_LOCALES) {
    if (fallbackLocale === locale) continue;
    const fallbackCv = state.jobForm.profileCvByLocale[fallbackLocale];
    const fallbackMostRecent = deriveSortedCvEntries({
      workExperiences: fallbackCv.profileWorkExperiences,
      education: fallbackCv.profileEducation,
      projects: fallbackCv.profileProjects,
    }).mostRecentWorkExp;
    if (fallbackMostRecent) return fallbackMostRecent;
  }

  return null;
}

/** CV field -> profile section it stamps a "last saved" timestamp for. */
const CV_FIELD_TO_SECTION: Partial<Record<keyof ProfileCvContent, ProfileSection>> = {
  profileTitle: 'header',
  profileSummary: 'summary',
  profileWorkExperiences: 'experience',
  profileEducation: 'education',
  profileProjects: 'projects',
  profileSkills: 'skills',
};

const jobFormSlice = createSlice({
  name: 'jobForm',
  initialState,
  reducers: {
    setField: (state, action: PayloadAction<{ field: keyof JobFormState; value: any }>) => {
      const { field, value } = action.payload;
      (state as any)[field] = value;
      if (field === 'profilePhotoDataUrl' || field === 'linkedInUrl') {
        stampSectionUpdated(state, 'header');
      } else if (field === 'homeAddress' || field === 'preferredCommuteMode') {
        stampSectionUpdated(state, 'homeAddress');
      }
    },
    /** Sets one CV field (title/experience/education/projects/skills) within a specific language's bucket. */
    setProfileCvField: (
      state,
      action: PayloadAction<{ locale: ProfileLocale; field: keyof ProfileCvContent; value: any }>
    ) => {
      const { locale, field, value } = action.payload;

      // Current role is derived, not asked: when the work-experience list changes, follow the
      // most recent entry's title unless the user has already typed a custom currentJob.title
      // that differs from what that derivation would have produced. Captured BEFORE the
      // assignment below, since that's when "previous most recent" still means anything.
      let previousMostRecentTitle: string | undefined;
      if (field === 'profileWorkExperiences') {
        previousMostRecentTitle = deriveSortedCvEntries({
          workExperiences: state.profileCvByLocale[locale].profileWorkExperiences,
          education: [],
          projects: [],
        }).mostRecentWorkExp?.title;
      }

      (state.profileCvByLocale[locale] as any)[field] = value;

      if (field === 'profileWorkExperiences') {
        const derivedTitle =
          deriveSortedCvEntries({
            workExperiences: value as ProfileWorkExperience[],
            education: [],
            projects: [],
          }).mostRecentWorkExp?.title ?? '';
        if (!state.currentJob.title || state.currentJob.title === (previousMostRecentTitle ?? '')) {
          state.currentJob.title = derivedTitle;
        }
      }

      const section = CV_FIELD_TO_SECTION[field];
      if (section) stampSectionUpdated(state, section);
    },
    setCurrentJobField: (state, action: PayloadAction<{ field: keyof JobDetails; value: any }>) => {
      const { field, value } = action.payload;
      (state.currentJob as any)[field] = value;
      if (field === 'salary' || field === 'workPercentage') {
        stampSectionUpdated(state, 'currentPay');
      }
    },
    setDesiredJobField: (state, action: PayloadAction<{ field: keyof JobFormState['desiredJob']; value: any }>) => {
      const { field, value } = action.payload;
      (state.desiredJob as any)[field] = value;
      if (field === 'locations' || field === 'regionTags') {
        const normalized = normalizeDesiredLocations(state.desiredJob);
        state.desiredJob.regionTags = normalized.regionTags;
        state.desiredJob.locations = normalized.locations;
        if (normalized.impliedRemoteness && state.desiredJob.remoteness.length === 0) {
          state.desiredJob.remoteness = [normalized.impliedRemoteness];
        }
      }
      stampSectionUpdated(state, 'matching');
    },
    setCompanyField: (state, action: PayloadAction<{ field: keyof Company; value: any }>) => {
      const { field, value } = action.payload;
      state.currentJob.company[field] = value;
    },
    /** Sets or clears (pass null) the saved commute-search starting point. */
    setHomeAddress: (state, action: PayloadAction<HomeAddress | null>) => {
      state.homeAddress = action.payload;
      stampSectionUpdated(state, 'homeAddress');
    },
    /** Null restores the default (public transport). */
    setPreferredCommuteMode: (state, action: PayloadAction<CommuteMode | null>) => {
      state.preferredCommuteMode = action.payload;
      stampSectionUpdated(state, 'homeAddress');
    },
    setApplicationDetailsField: (
      state,
      action: PayloadAction<{ field: keyof ApplicationDetails; value: any }>
    ) => {
      const { field, value } = action.payload;
      (state.applicationDetails as any)[field] = value;
      stampSectionUpdated(state, 'applying');
    },
    addBenefit: (state, action: PayloadAction<string>) => {
      state.currentJob.benefits.push(action.payload);
    },
    removeBenefit: (state, action: PayloadAction<number>) => {
      state.currentJob.benefits.splice(action.payload, 1);
    },
    addDesiredBenefit: (state, action: PayloadAction<string>) => {
      if (!state.desiredJob.benefits.includes(action.payload)) {
        state.desiredJob.benefits.push(action.payload);
      }
      stampSectionUpdated(state, 'matching');
    },
    removeDesiredBenefit: (state, action: PayloadAction<string>) => {
      state.desiredJob.benefits = state.desiredJob.benefits.filter((b) => b !== action.payload);
      stampSectionUpdated(state, 'matching');
    },
    /** Replaces the full language list (add/edit/remove all go through this) and mirrors names into currentJob.languages for calculate-job-match-score.ts. */
    setProfileLanguages: (state, action: PayloadAction<ProfileLanguage[]>) => {
      state.profileLanguages = action.payload;
      state.currentJob.languages = mirrorLanguagesIntoCurrentJob(action.payload);
      stampSectionUpdated(state, 'languages');
    },
    setProfileLicences: (state, action: PayloadAction<string[]>) => {
      state.profileLicences = action.payload;
      stampSectionUpdated(state, 'licences');
    },
    setAvailabilityField: (
      state,
      action: PayloadAction<{ field: keyof Availability; value: any }>
    ) => {
      const { field, value } = action.payload;
      (state.availability as any)[field] = value;
      stampSectionUpdated(state, 'applying');
    },
    setRecruiterVisibility: (state, action: PayloadAction<RecruiterVisibility>) => {
      state.recruiterVisibility = action.payload;
    },
    resetForm: () => initialState,
    /** Adopts the server's copy of the profile form (e.g. after a user-state sync conflict) so local state converges instead of re-fighting the server forever. Only overwrites fields the server actually sent. */
    applyServerJobForm: (state, action: PayloadAction<Partial<JobFormState>>) => {
      const server = action.payload;
      if (typeof server.profilePhotoDataUrl === 'string') state.profilePhotoDataUrl = server.profilePhotoDataUrl;
      if (typeof server.cvFileKey === 'string') state.cvFileKey = server.cvFileKey;
      if (typeof server.linkedInUrl === 'string') state.linkedInUrl = server.linkedInUrl;
      // Server payloads can predate any of the newer fields (per-locale CV buckets, profileSummary,
      // workPercentage, the 2026-09 profile redesign fields), so coerce every nested shape the same
      // way the REHYDRATE migration does instead of trusting it as-is.
      const legacy = server as any;
      if (server.profileCvByLocale && typeof server.profileCvByLocale === 'object') {
        state.profileCvByLocale = buildProfileCvByLocaleFromPersisted(legacy);
      } else if (legacy.profileTitle !== undefined || Array.isArray(legacy.profileWorkExperiences) || Array.isArray(legacy.profileSkills)) {
        // Pre-per-locale payload: the old flat CV fields become the English bucket.
        state.profileCvByLocale = buildProfileCvByLocaleFromPersisted(legacy);
      }
      if (server.currentJob && typeof server.currentJob === 'object') {
        state.currentJob = {
          ...initialJobDetails,
          ...server.currentJob,
          workPercentage: server.currentJob.workPercentage ?? 100,
          languages: Array.isArray(server.currentJob.languages) ? server.currentJob.languages : [],
        };
      }
      if (server.desiredJob && typeof server.desiredJob === 'object') {
        state.desiredJob = coerceDesiredJob(server.desiredJob);
      }
      if (server.homeAddress !== undefined) {
        state.homeAddress = coerceHomeAddress(server.homeAddress);
      }
      if (server.preferredCommuteMode !== undefined) {
        state.preferredCommuteMode = coerceCommuteMode(server.preferredCommuteMode);
      }
      if (server.applicationDetails && typeof server.applicationDetails === 'object') {
        state.applicationDetails = coerceApplicationDetails(server.applicationDetails);
      }
      if (server.profileLanguages !== undefined) {
        state.profileLanguages = coerceProfileLanguages(server.profileLanguages);
        state.currentJob.languages = mirrorLanguagesIntoCurrentJob(state.profileLanguages);
      } else if (state.profileLanguages.length === 0 && state.currentJob.languages.length > 0) {
        // Legacy profile: only the old flat language list exists, seed the structured one from it.
        state.profileLanguages = seedProfileLanguagesFromCurrentJob([], state.currentJob.languages);
      }
      if (server.profileLicences !== undefined) {
        state.profileLicences = coerceProfileLicences(server.profileLicences);
      }
      if (server.availability !== undefined) {
        state.availability = coerceAvailability(server.availability);
      }
      if (server.recruiterVisibility !== undefined) {
        state.recruiterVisibility = coerceRecruiterVisibility(server.recruiterVisibility);
      }
      if (server.sectionUpdatedAt !== undefined) {
        state.sectionUpdatedAt = coerceSectionUpdatedAt(server.sectionUpdatedAt);
      }
      if (server.cvTemplateId !== undefined) state.cvTemplateId = coerceCvTemplateId(server.cvTemplateId);
      if (server.cvCheckDismissed !== undefined) state.cvCheckDismissed = coerceStringList(server.cvCheckDismissed);
      if (server.cvVersions !== undefined) state.cvVersions = coerceCvVersions(server.cvVersions);
      if (server.cvStyle !== undefined) state.cvStyle = coerceCvStyle(server.cvStyle);
    },
  },
  extraReducers: (builder) => {
    // Cross-slice: a comparisonPreferences importance change also stamps this slice's
    // sectionUpdatedAt.importance, so the "Saved just now" line works for the ImportanceSheet
    // without comparisonPreferencesSlice needing to know about sectionUpdatedAt itself.
    builder.addCase('comparisonPreferences/setImportance', (state) => {
      stampSectionUpdated(state, 'importance');
    });
    // Handle REHYDRATE to migrate old state structure
    builder.addCase(REHYDRATE, (state, action: any) => {
      const persistedState = action.payload?.jobForm;
      if (persistedState) {
        // If state doesn't have currentJob, migrate it
        if (!persistedState.currentJob || typeof persistedState.currentJob !== 'object') {
          return migrateState(persistedState);
        }
        // Migrate salary fields if they still use old structure
        const migratedCurrentJob = persistedState.currentJob ? {
          ...initialJobDetails,
          ...persistedState.currentJob,
          salary: persistedState.currentJob.salary !== undefined
            ? persistedState.currentJob.salary
            : (persistedState.currentJob.salaryRangeMin !== undefined
              ? persistedState.currentJob.salaryRangeMin
              : 0),
          workPercentage: persistedState.currentJob.workPercentage ?? 100,
        } : initialJobDetails;

        const migratedDesiredJob = persistedState.desiredJob ? {
          ...persistedState.desiredJob,
          salaryMin: persistedState.desiredJob.salaryMin !== undefined
            ? persistedState.desiredJob.salaryMin
            : (persistedState.desiredJob.salaryRangeMin !== undefined
              ? persistedState.desiredJob.salaryRangeMin
              : 0),
        } : initialState.desiredJob;

        // Ensure all required fields exist (including CV sections)
        return {
          ...initialState,
          ...persistedState,
          currentJob: migratedCurrentJob,
          desiredJob: coerceDesiredJob(migratedDesiredJob),
          profilePhotoDataUrl: persistedState.profilePhotoDataUrl || '',
          cvFileKey: persistedState.cvFileKey || '',
          linkedInUrl: persistedState.linkedInUrl || '',
          profileCvByLocale: buildProfileCvByLocaleFromPersisted(persistedState),
          homeAddress: coerceHomeAddress(persistedState.homeAddress),
          preferredCommuteMode: coerceCommuteMode(persistedState.preferredCommuteMode),
          applicationDetails: coerceApplicationDetails(persistedState.applicationDetails),
          profileLanguages: seedProfileLanguagesFromCurrentJob(
            coerceProfileLanguages(persistedState.profileLanguages),
            Array.isArray(migratedCurrentJob.languages) ? migratedCurrentJob.languages : []
          ),
          profileLicences: coerceProfileLicences(persistedState.profileLicences),
          availability: coerceAvailability(persistedState.availability),
          recruiterVisibility: coerceRecruiterVisibility(persistedState.recruiterVisibility),
          sectionUpdatedAt: coerceSectionUpdatedAt(persistedState.sectionUpdatedAt),
          cvTemplateId: coerceCvTemplateId(persistedState.cvTemplateId),
          cvCheckDismissed: coerceStringList(persistedState.cvCheckDismissed),
          cvVersions: coerceCvVersions(persistedState.cvVersions),
          cvStyle: coerceCvStyle(persistedState.cvStyle),
        };
      }
      return state;
    });
  },
});

export const {
  setField,
  setProfileCvField,
  setCurrentJobField,
  setDesiredJobField,
  setCompanyField,
  setHomeAddress,
  setPreferredCommuteMode,
  setApplicationDetailsField,
  addBenefit,
  removeBenefit,
  addDesiredBenefit,
  removeDesiredBenefit,
  setProfileLanguages,
  setProfileLicences,
  setAvailabilityField,
  setRecruiterVisibility,
  resetForm,
  applyServerJobForm,
} = jobFormSlice.actions;
export default jobFormSlice.reducer;
